import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchRooms, fetchRoomMessages } from '@/redux/thunks/chatThunks';

export const MEGAPHONE_ROOM_ID = 'megaphone';

export interface ChatRoom {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  type: string;
  sort_order: number;
}

export type MegaphoneScope = 'local' | 'global';

export interface ChatMessage {
  _id: string;
  roomId: string;
  userId: string;
  username: string;
  displayName: string;
  avatar?: string;
  body: string;
  isMegaphone: boolean;
  megaTxHash?: string;
  megaScope?: MegaphoneScope;
  originRoomId?: string;
  originRoomSlug?: string;
  originRoomName?: string;
  createdAt: string;
}

interface ChatState {
  rooms: ChatRoom[];
  activeRoomId: string | null;
  messagesByRoom: Record<string, ChatMessage[]>;
  hasMoreByRoom: Record<string, boolean>;
  roomsLoading: boolean;
  messagesLoading: boolean;
  /** Per-room cursor for pagination (ISO date of oldest loaded message) */
  cursorByRoom: Record<string, string | undefined>;
}

const initialState: ChatState = {
  rooms: [],
  activeRoomId: null,
  messagesByRoom: {},
  hasMoreByRoom: {},
  roomsLoading: false,
  messagesLoading: false,
  cursorByRoom: {},
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setActiveRoom(state, action: PayloadAction<string | null>) {
      state.activeRoomId = action.payload;
    },
    /**
     * Called by the socket listener when a new message arrives.
     *
     * Megaphones get mirrored into the special MEGAPHONE_ROOM_ID
     * aggregation room so the dedicated megaphones view stays live
     * without a separate fetch on each broadcast.
     */
    addMessage(state, action: PayloadAction<ChatMessage>) {
      const { roomId, isMegaphone } = action.payload;
      if (!state.messagesByRoom[roomId]) state.messagesByRoom[roomId] = [];
      const exists = state.messagesByRoom[roomId].some((m) => m._id === action.payload._id);
      if (!exists) state.messagesByRoom[roomId].push(action.payload);

      if (isMegaphone) {
        if (!state.messagesByRoom[MEGAPHONE_ROOM_ID]) state.messagesByRoom[MEGAPHONE_ROOM_ID] = [];
        const inAgg = state.messagesByRoom[MEGAPHONE_ROOM_ID].some((m) => m._id === action.payload._id);
        if (!inAgg) state.messagesByRoom[MEGAPHONE_ROOM_ID].push(action.payload);
      }
    },
    clearRoomMessages(state, action: PayloadAction<string>) {
      delete state.messagesByRoom[action.payload];
      delete state.hasMoreByRoom[action.payload];
      delete state.cursorByRoom[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchRooms
      .addCase(fetchRooms.pending, (state) => { state.roomsLoading = true; })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        state.rooms = action.payload;
        state.roomsLoading = false;
        // Auto-select first room if none active
        if (!state.activeRoomId && action.payload.length > 0) {
          state.activeRoomId = action.payload[0].id;
        }
      })
      .addCase(fetchRooms.rejected, (state) => { state.roomsLoading = false; })

      // fetchRoomMessages
      .addCase(fetchRoomMessages.pending, (state) => { state.messagesLoading = true; })
      .addCase(fetchRoomMessages.fulfilled, (state, action) => {
        const { roomId, messages, hasMore, append } = action.payload;
        state.messagesLoading = false;
        if (append) {
          const existing = state.messagesByRoom[roomId] || [];
          state.messagesByRoom[roomId] = [...messages, ...existing];
        } else {
          state.messagesByRoom[roomId] = messages;
        }
        state.hasMoreByRoom[roomId] = hasMore;
        if (messages.length > 0) {
          state.cursorByRoom[roomId] = messages[0].createdAt;
        }
      })
      .addCase(fetchRoomMessages.rejected, (state) => { state.messagesLoading = false; });
  },
});

export const { setActiveRoom, addMessage, clearRoomMessages } = chatSlice.actions;
export default chatSlice.reducer;
